import { Button, Group, Space, Text } from '@mantine/core';
import { useContext } from 'react';
import { useCookies } from 'react-cookie';
import { ReactFormBuilder } from 'bookitup-form-builder';
import { IntlProvider } from 'react-intl';
import { IconInfoCircle, IconPlus } from '@tabler/icons';
import { i18n } from '@lingui/core';
import { useNavigate } from 'react-router-dom';
import { BookitupFormContext } from '../../../../Service/Context/BookitupFormContext';
import FormBuilderLocale from '../lang/FormBuilderLocale';
import '../style/application.scss';
import ChangesDetectedAlert from '../ChangesDetectedAlert';
import RequiredFieldsAlert from '../RequiredFieldsAlert';
import BookitupAlert from '../../../../Atoms/BookitupAlert';
import { ThemeTitle } from '../../../../Atoms/ThemeText';
import { useEventKinds } from '../../../../Service/hooks/useEventKinds';
import { useBookitupTheme } from '../../../../Service/hooks/useBookitupTheme';

const FormBuilder = ({ toolbarItems }) => {
  const { bookitupForm, updateForm, addCustomItem } = useContext(BookitupFormContext);
  const [locale] = useCookies(['locale']);
  const navigate = useNavigate();
  const { kindNames } = useEventKinds();
  const [colorScheme] = useBookitupTheme();

  if (!bookitupForm && kindNames.length === 0) {
    return null;
  }

  const { task_data, outOfSync } = bookitupForm;
  const usableLocale = locale.locale === 'en' ? 'en' : 'de';
  const currentAppLocale = FormBuilderLocale[usableLocale];

  return (
    <IntlProvider defaultLocale="en" locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
      <div>
        <link rel="stylesheet" href="https://use.fontawesome.com/releases/v6.2.1/css/all.css" />
        <link
          rel="stylesheet"
          href="https://stackpath.bootstrapcdn.com/bootstrap/4.4.1/css/bootstrap.min.css"
          integrity="sha384-Vkoo8x4CGsO3+Hhxv8T/Q5PaXtkKtu6ug5TOeNV6gBiFeWPGFN9MuhOf23Q9Ifjh"
          crossOrigin="anonymous"
        />
      </div>

      <BookitupAlert title="Tipp: So fügst Du individuelle Felder hinzu" icon={<IconInfoCircle />}>
        <Text mb="sm">
          Wenn du weitere individuelle Felder hinzufügen möchtest, kannst Du Dir einfach unter{' '}
          <Text
            span
            weight="bolder"
            color="blue"
            variant="link"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/settings/customfields')}
          >
            eigene Felder
          </Text>{' '}
          individuelle Felder erstellen. Anschließend kannst Du diese unter "Verfügbare Felder" einfach dem
          Kontaktformular hinzufügen.
        </Text>
        <Text>Damit lassen sich z.B. auch Felder mit Checkboxen oder Dropdowns hinzufügen.</Text>
      </BookitupAlert>
      <Space h="xs" />

      {outOfSync && <ChangesDetectedAlert />}
      <RequiredFieldsAlert />
      <ThemeTitle order={3} mb="xs" mt="xs">
        {' '}
        Vorgefertigte Layouts
      </ThemeTitle>
      <Group
        p="sm"
        mb={45}
        sx={(theme) => ({
          border: `2px dashed ${theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[6]}`,
        })}
      >
        {[
          'DATUM_START_END',
          'DATE_AND_EVENT_KIND',
          'START_END',
          'FIRST_AND_LASTNAME',
          'SALUTATION_FIRST_AND_LAST_NAME',
          'PHONE_AND_EMAIL',
          'ZIP_AND_CITY',
          'PARTNER_FIRST_AND_LASTNAME',
          'PARTNER_SALUTATION_FIRST_AND_LAST_NAME',
          'PARTNER_PHONE_AND_EMAIL',
        ].map((customItem) => (
          <Button
            key={customItem}
            leftIcon={<IconPlus size={16} />}
            onClick={() => addCustomItem(customItem)}
            size="xs"
            variant="default"
          >
            {i18n._(`form.custom.field.${customItem}`)}
          </Button>
        ))}
      </Group>
      {toolbarItems && (
        <ReactFormBuilder
          saveAlways
          toolbarItems={toolbarItems}
          data={task_data}
          onPost={(data) => updateForm({ task_data: data.task_data })}
          appLocaleOverride={FormBuilderLocale}
          locale={usableLocale}
          bookitupCtx={{
            kinds: kindNames,
            theme: colorScheme,
          }}
        />
      )}
    </IntlProvider>
  );
};

export default FormBuilder;
